import { useState, useEffect, useCallback } from 'react';

/**
 * Détection et déclenchement de l'installation PWA.
 *
 * - Android/Chrome : capture `beforeinstallprompt` → installation en 1 clic.
 * - iOS (Safari/Chrome, moteur WebKit) : pas de prompt natif — `isIOS` permet
 *   d'afficher un guide « Ajouter à l'écran d'accueil ».
 */

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Capturé au niveau module : Chrome émet l'événement très tôt, souvent avant
// le montage des composants React.
let capturedPrompt: BeforeInstallPromptEvent | null = null;
const promptListeners = new Set<(e: BeforeInstallPromptEvent) => void>();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    capturedPrompt = e as BeforeInstallPromptEvent;
    promptListeners.forEach((listener) => listener(capturedPrompt as BeforeInstallPromptEvent));
  });
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function isIOSDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIPad = ua.includes('Macintosh') && typeof document !== 'undefined' && 'ontouchend' in document;
  return /iPhone|iPad|iPod/i.test(ua) || isIPad;
}

interface PWAInstallResult {
  /** Prompt natif Chrome disponible (Android/desktop). */
  isInstallable: boolean;
  /** L'app tourne déjà en mode installé (standalone). */
  isInstalled: boolean;
  /** Appareil iOS : installation manuelle via le menu partage. */
  isIOS: boolean;
  /** Déclenche le prompt natif. Retourne true si l'utilisateur accepte. */
  promptInstall: () => Promise<boolean>;
}

export function usePWAInstall(): PWAInstallResult {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(capturedPrompt);
  const [installed, setInstalled] = useState<boolean>(isStandalone());

  useEffect(() => {
    const onPrompt = (e: BeforeInstallPromptEvent) => setInstallPrompt(e);
    promptListeners.add(onPrompt);

    const onInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
      capturedPrompt = null;
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      promptListeners.delete(onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<boolean> => {
    if (!installPrompt) return false;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === 'accepted') {
      setInstallPrompt(null);
      capturedPrompt = null;
      return true;
    }
    return false;
  }, [installPrompt]);

  return {
    isInstallable: installPrompt !== null && !installed,
    isInstalled: installed,
    isIOS: isIOSDevice(),
    promptInstall,
  };
}

export default usePWAInstall;
